import { Divider } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import { SleepRecorderDetails } from '@frontend/components/elements/SleepRecorderDetails';
import type { ICustomControlRequestReadProps } from '../../interfaces/customControlRequestReadProps';
import { getServiceLabel } from '../../../../../../common/customControls/getServiceLabel';
import { RequestStatus } from '../../../../../../common/enums/requestStatus';
import { SomnologyOrganizationType } from '../../../../../../common/enums/somnologyOrganizationType';
import { calculateBmi } from '../../../../../../common/utils/bmi';
import { getDisplayName } from '../../../../../../common/utils/getDisplayName';
import { translateKey } from '../../../../../translate';
import { RequestReadButtons } from '../../../../elements/carePathButtons/RequestReadButtons';
import { FormParagraph, UserTextParagraph } from '../../../../elements/common';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroupRead } from '../../../../elements/forms/horizontalFormGroupRead/HorizontalFormGroupRead';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { ProcessContainer } from '../../../episodes/common';
import { ProcessItemFooter } from '../../../episodes/ProcessItemFooter';
import { ProcessItemHeader } from '../../../episodes/ProcessItemHeader';
import { Osasc1V1DiagnosticReportReadCustomControlFields } from '../../OSASC1/v1/Osasc1V1DiagnosticReportReadCustomControlFields';
import { CustomControlCollapseBody } from '../../utils/v1/CustomControlCollapseBody';
import { OsasvV1FollowingSteps } from './OsasvV1FollowingSteps';
import { RequestSummary } from './RequestSummary';

export function OsasvV1RequestRead(props: ICustomControlRequestReadProps) {
    const [isFooterVisible, setIsFooterVisible] = useState(false);

    useEffect(() => {
        checkIsFooterVisible();
    }, [props.request.customControlData]);

    return (
        <ProcessContainer>
            <ProcessItemHeader
                date={props.process.dateSent || (props.request && props.request.dateSent)}
                isFirstItem={props.isFirstItem}
                isFirstProcess={props.itemState.isFirstProcess}
                isItemOpen={props.itemState.isOpen}
                onToggle={async (isOpened) => {
                    await props.updateItemState({ ...props.itemState, isOpen: isOpened });
                }}
                organizationName={props.process.requesterOrganization?.name}
                practitionerName={
                    props.process.requesterPractitioner
                        ? getDisplayName(props.process.requesterPractitioner)
                        : undefined
                }
                process={props.process}
                serviceType={props.process.serviceType}
                title={getServiceLabel(props.process.code, 'request', props.process.name)}
            />

            <RequestSummary {...props} />

            <CustomControlCollapseBody isOpen={props.itemState.isOpen}>
                {props.request.customControlData.somnologyOrganizationType === SomnologyOrganizationType.nsi &&
                    props.request.customControlData.voorgeschiedenis && (
                        <HorizontalFormGroupRead>
                            <FormLabel>Voorgeschiedenis</FormLabel>
                            <InputContainer>
                                <UserTextParagraph>
                                    {props.request.customControlData.voorgeschiedenis}
                                </UserTextParagraph>
                            </InputContainer>
                        </HorizontalFormGroupRead>
                    )}

                {props.request.customControlData.slaapapneu_in_de_1e_graad && (
                    <HorizontalFormGroupRead>
                        <FormLabel>Slaapapneu in de 1e graad</FormLabel>
                        <InputContainer>
                            <UserTextParagraph>
                                {props.request.customControlData.slaapapneu_in_de_1e_graad}
                            </UserTextParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                <SleepRecorderDetails
                    distributionMethod={props.request.customControlData.slaaprecorderVerzenden}
                    patientHasSmartphone={props.request.customControlData.patientHasSmartphone}
                    sleepRecorderType={props.request.customControlData.typeSlaaprecorder}
                />

                {props.request.customControlData.contactgegevens_patient.patient_informatie && (
                    <HorizontalFormGroupRead>
                        <FormLabel>Patiëntinformatie</FormLabel>
                        <InputContainer>
                            <UserTextParagraph>
                                {props.request.customControlData.contactgegevens_patient.patient_informatie}
                            </UserTextParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                {renderBmi(props.request.customControlData.gewicht, props.request.customControlData.lengte)}

                {props.request.status === RequestStatus.draft && props.request.customControlData.consult && (
                    <Osasc1V1DiagnosticReportReadCustomControlFields
                        customControlData={props.request.customControlData.consult}
                    />
                )}

                <Divider />

                <OsasvV1FollowingSteps />
            </CustomControlCollapseBody>

            {isFooterVisible && (
                <ProcessItemFooter
                    isItemOpen={props.itemState.isOpen}
                    labelLess={translateKey('request.hideReferral')}
                    labelMore={translateKey('request.showReferral')}
                    onToggle={async (isOpened) => await props.updateItemState({ ...props.itemState, isOpen: isOpened })}
                />
            )}

            <RequestReadButtons {...props} />
        </ProcessContainer>
    );

    function checkIsFooterVisible() {
        if (
            props.request.customControlData.voorgeschiedenis ||
            props.request.customControlData.slaapapneu_in_de_1e_graad ||
            props.request.customControlData.slaaprecorderVerzenden
        ) {
            setIsFooterVisible(true);
        }
    }
}

export function renderBmi(gewicht: number | null | undefined, lengte: number | null | undefined) {
    if (lengte === null || lengte === undefined || gewicht === null || gewicht === undefined) {
        return <></>;
    }

    return (
        <HorizontalFormGroupRead>
            <FormLabel>BMI</FormLabel>
            <InputContainer>
                <FormParagraph>{calculateBmi(gewicht, lengte)}</FormParagraph>
            </InputContainer>
        </HorizontalFormGroupRead>
    );
}
